import React, { useMemo } from "react";
import { StyleProp, StyleSheet, ViewStyle, View } from "react-native";
import { useTheme } from "../hooks/use-theme";
import { ScheduleTimeType, ScheduleType } from "../__generated__/graphql";
import { TimeSheetFieldsFragment } from "../__generated__/operations/graphql";
import { minutesAsString } from "../utils";
import Label from "../ui_atoms/Label";
import { useI18n } from "../hooks/i18n-hooks";
import Text from "./Text";
import { useDateFormatter } from "../hooks/date";

type ScheduleTimeProps = {
    time: ScheduleType;
};

const ScheduleTime: React.FC<ScheduleTimeProps> = ({ time }) => {
    const styles = useStyles();
    const i18n = useI18n();
    const { colors } = useTheme();

    const labelMode = useMemo(() => {
        if (time.type === ScheduleTimeType.Work) {
            return null;
        }
        return "default";
    }, [time.type]);

    const textColor = useMemo(() => {
        if (time.type === ScheduleTimeType.Work) {
            return colors.primary;
        }
        return colors.onSurfaceDisabled;
    }, [time.type, colors]);

    return (
        <View style={styles.scheduleRow} key={`${time.startTime}-${time.endTime}`}>
            <Text style={[styles.rowTimeText, { color: textColor }]}>
                {minutesAsString(time.startTime)}
            </Text>
            <Text style={[styles.rowTimeMiddle, { color: textColor }]}>-</Text>
            <Text style={[styles.rowTimeText, { color: textColor }]}>
                {minutesAsString(time.endTime)}
            </Text>
            <Text style={styles.rowTimeSep} />
            <View style={styles.rowLabel}>
                {labelMode && (
                    <Label mode={labelMode} text={i18n.t(`workingTime.type.${time.type}`)} />
                )}
            </View>
        </View>
    );
};

type TrackingDatesDisplayProps = {
    trackingDates: TimeSheetFieldsFragment["trackingDates"];
    style?: StyleProp<ViewStyle>;
};

const TrackingDatesDisplay: React.FC<TrackingDatesDisplayProps> = ({ trackingDates, style }) => {
    const styles = useStyles();
    const intlFormat = useDateFormatter();

    return (
        <View style={[styles.container, style]}>
            {trackingDates.map((trackingDate) => (
                <View style={styles.cell} key={trackingDate.date}>
                    <View style={styles.dateHeader}>
                        <Text style={styles.dateText}>
                            {intlFormat(new Date(trackingDate.date), {
                                weekday: "long",
                                day: "numeric",
                                month: "long",
                            })}
                        </Text>
                        <Text style={styles.durationText}>
                            &nbsp;(
                            {minutesAsString(
                                trackingDate.daySchedule?.fixedTimes?.reduce(
                                    (acc, t) => acc + (t.endTime - t.startTime),
                                    0,
                                ) || 0,
                            )}
                            )
                        </Text>
                    </View>
                    <View style={styles.times}>
                        {trackingDate.daySchedule?.fixedTimes?.map((time) => (
                            <ScheduleTime key={time.startTime} time={time} />
                        ))}
                    </View>
                </View>
            ))}
        </View>
    );
};

const useStyles = () => {
    const { colors, fonts, gridSize } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                container: {},
                cell: {
                    flexDirection: "column",
                    paddingVertical: gridSize / 2,
                    borderBottomWidth: 1,
                    borderColor: colors.accentLighter,
                },
                scheduleRow: {
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    paddingVertical: 2,
                },
                rowTimeText: {
                    ...fonts.bodyMedium,
                    flexGrow: 0,
                    textAlign: "left",
                },
                rowTimeMiddle: {
                    width: 10,
                    textAlign: "center",
                },
                rowTimeSep: {
                    ...fonts.bodyMedium,
                    flexGrow: 1,
                },
                rowLabel: {
                    flexGrow: 0,
                },
                absence: {
                    color: colors.onSurfaceDisabled,
                },
                dateText: {
                    ...fonts.bodyLarge,
                },
                durationText: {
                    ...fonts.bodyMedium,
                },
                dateHeader: {
                    flexDirection: "row",
                },
                times: {},
                dateTimes: {
                    flexDirection: "column",
                },
            }),
        [
            colors.accentLighter,
            colors.onSurfaceDisabled,
            fonts.bodyLarge,
            fonts.bodyMedium,
            gridSize,
        ],
    );
};

export default TrackingDatesDisplay;
